import apiClient from '@skygroup/shared/API/apiClient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setDiamondsWallet,
  setIsUpgradePlanModalOpen,
  updateAgentInfo,
} from '../store/config';
import endpoints from '../utils/endpoints';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { fetchDiamondsWallet } from './WalletApi';
import { Permissions } from '@skygroup/shared/constants/permissions';

const agentInfoApi = (token: string) => {
  return apiClient
    .post(`${endpoints.AGENT.POST_INFO}`, { token: token })
    .then((res) => {
      return res.data;
    });
};

export const useLoadAgentUser = () => {
  const dispatch = useDispatch();
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [agentInfo, setAgentInfo] = useState<any>({});
  const [modelId, setModelId] = useState<string | number | null>(null);
  const navigate = useNavigate();
  const hasLoadedAgentInfoRef = useRef(false);

  // fetch & set wallet using modelId (agency id) — only called for Agency Admin
  const fetchDiamondsWalletAndSet = useCallback(
    async (agencyModelId: string | number) => {
      const notAgencyAdmin =
        agentInfo?.agency?.role !== Permissions.ROLE_AGENCY_ADMIN;
      if (notAgencyAdmin) {
        return;
      }

      try {
        const diamondsWallet = await fetchDiamondsWallet(agencyModelId);
        if (diamondsWallet) {
          dispatch(setDiamondsWallet(diamondsWallet));
        }
      } catch (e) {
        console.warn('fetchDiamondsWallet failed', e);
      }
    },
    [dispatch, agentInfo]
  );

  const getAgentInfo = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const token =
        searchParams.get('t') || localStorage.getItem('agent-admin-token');
      if (!token) {
        return;
      }

      //when we load token - we want to remove it from url ASAP
      if (searchParams.get('t')) {
        const newUrl = window.location.pathname;
        navigate(newUrl, { replace: true });
      }

      setIsAgentLoading(true);
      let userInfoRes: any = await agentInfoApi(token);

      if (userInfoRes.status === 'success') {
        const priceViewLocalStorage = JSON.parse(
          localStorage.getItem('price_view') || '{}'
        );
        const priceView = isEmpty(priceViewLocalStorage)
          ? userInfoRes.data.agent.price_view
          : priceViewLocalStorage;

        const payment_status =
          userInfoRes.data.agency?.subscription?.payment_status;

        // if (payment_status === null || (payment_status && payment_status === 'none')) {
        //  Question for Nir: MAYBE WE SHOULD ALSO CHECK FOR payment_status === null? like in example above?
        if (payment_status && payment_status === 'none') {
          // this means user not paid yet - force him to free
          // TODO: use ENUM instead of hardcoded string value "agent_360_light"
          userInfoRes.data.agency.subscription.plan_id = 'agent_360_light';
        } else if (
          payment_status &&
          (payment_status === 'fail' || payment_status === 'fail_force')
        ) {
          dispatch(
            setIsUpgradePlanModalOpen({
              type: 'update-card',
              data: { disabledClose: payment_status === 'fail_force' },
            })
          );
        }

        const agentInformation = {
          ...userInfoRes.data,
          agentMode: true,
          agent: {
            ...userInfoRes.data.agent,
            price_view: priceView,
          },
        };
        dispatch(updateAgentInfo(agentInformation));
        setAgentInfo(agentInformation);
        localStorage.setItem('agent-admin-token', token);
        setIsAgentLoading(false);
        return userInfoRes;
      } else {
        dispatch(updateAgentInfo({}));
        setAgentInfo({});
        localStorage.removeItem('agent-admin-token');
        localStorage.removeItem('price_view');
        setIsAgentLoading(false);
        return null;
      }
    } catch (error: any) {
      dispatch(updateAgentInfo({}));
      setAgentInfo({});
      localStorage.removeItem('agent-admin-token');
      localStorage.removeItem('price_view');
      setIsAgentLoading(false);
      return null;
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    // Only load agent info once on mount, not on every route change
    // Check if we already have agent info loaded or if there's a token in URL (initial load)
    const searchParams = new URLSearchParams(window.location.search);
    const hasTokenInUrl = searchParams.get('t') !== null;

    // Only run if:
    // 1. We haven't loaded agent info yet, OR
    // 2. There's a token in the URL (initial load that needs processing)
    if (hasLoadedAgentInfoRef.current && !hasTokenInUrl) {
      return;
    }

    getAgentInfo()
      .then((userInfoRes: any) => {
        if (!userInfoRes) return;
        hasLoadedAgentInfoRef.current = true;

        const isAgencyAdmin =
          userInfoRes?.data?.agency?.role === 'Agency Admin';
        if (!isAgencyAdmin) return;

        const extractedModelId =
          userInfoRes?.data?.agency?.agency_id ??
          userInfoRes?.data?.agency ??
          null;
        if (!extractedModelId) return;

        setModelId(extractedModelId);
        return fetchDiamondsWalletAndSet(extractedModelId);
      })
      .catch((err) => {
        console.warn(
          'getAgentInfo / fetchDiamondsWalletAndSet sequence failed:',
          err
        );
      });
  }, [getAgentInfo, fetchDiamondsWalletAndSet]);

  // Auto-refresh wallet every 10 minutes when modelId is available
  useEffect(() => {
    if (!modelId) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchDiamondsWalletAndSet(modelId);
    }, 10 * 60 * 1000); // 10 minutes in milliseconds

    return () => {
      clearInterval(intervalId);
    };
  }, [modelId, fetchDiamondsWalletAndSet]);

  return { isAgentLoading, agentInfo };
};
